/**
 * DedupDialog — find duplicate contact groups by email/name signature,
 * pick canonical, merge the rest. Multi-step orchestration on the
 * server (update canonical → reassign deals → archive merged). Each
 * step is independently auditable.
 */

import { useState } from 'react';
import {
  useFiasLeadsDuplicates,
  useMergeFiasLeadsContacts,
  useFiasLeadsContacts,
} from '../hooks/useFiasLeads';
import type { FiasLeadsContactDto } from '@/lib/api';
import { SkeletonBlock } from './Skeletons';

interface Props {
  holderId: string;
  onClose: () => void;
}

export function DedupDialog({ holderId, onClose }: Props) {
  const duplicatesQuery = useFiasLeadsDuplicates(holderId);
  const contactsQuery = useFiasLeadsContacts(holderId);
  const mergeMutation = useMergeFiasLeadsContacts(holderId);

  const contactById = new Map<string, FiasLeadsContactDto>(
    (contactsQuery.data ?? []).map((c) => [c.contactId, c]),
  );

  const [busy, setBusy] = useState<string | null>(null);

  const handleMerge = async (canonicalId: string, mergedId: string) => {
    setBusy(`${canonicalId}|${mergedId}`);
    try {
      await mergeMutation.mutateAsync({ canonicalId, mergedId });
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 p-4">
      <div className="w-full max-w-3xl rounded border border-border bg-card p-6 flex flex-col gap-3 max-h-[90vh] overflow-auto">
        <header className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold">Find duplicate contacts</h2>
            <p className="text-xs text-muted-foreground">
              Grouped by primary email or "first last" name signature.
            </p>
          </div>
          <button onClick={onClose} className="text-sm text-muted-foreground">
            Close
          </button>
        </header>

        {duplicatesQuery.isLoading && <SkeletonBlock lines={4} />}
        {duplicatesQuery.data && duplicatesQuery.data.length === 0 && (
          <p className="text-sm text-muted-foreground">No duplicates detected. 🎉</p>
        )}

        {(duplicatesQuery.data ?? []).map((group, gi) => (
          <div
            key={`${group.signature}-${gi}`}
            className="rounded border border-border p-3 flex flex-col gap-2"
          >
            <p className="text-xs text-muted-foreground">
              Signature: <code className="text-xs">{group.signature || '(empty)'}</code>
            </p>
            <ul className="divide-y divide-border">
              {group.contactIds.map((id, idx) => {
                const c = contactById.get(id);
                const others = group.contactIds.filter((other) => other !== id);
                return (
                  <li key={id} className="py-2 flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">
                        {c ? `${c.firstName} ${c.lastName ?? ''}`.trim() : id}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {c?.primaryEmail ?? '—'} · {c?.title ?? ''}
                      </p>
                    </div>
                    {idx === 0 && (
                      <span className="rounded bg-emerald-100 text-emerald-700 px-2 py-0.5 text-xs">
                        canonical
                      </span>
                    )}
                    {idx !== 0 && (
                      <button
                        onClick={() => handleMerge(group.contactIds[0], id)}
                        disabled={busy !== null}
                        className="rounded border border-border px-2 py-1 text-xs hover:bg-muted/30"
                      >
                        {busy === `${group.contactIds[0]}|${id}`
                          ? 'Merging…'
                          : `Merge into ${others[0]?.slice(0, 4) ?? 'top'}…`}
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}
